import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import "../Styling/TeamDetails.css";

const TeamDetails = () => {
  const { user } = useAuth();
  const userId = user?.userId;
  const orgId = user?.organisationId;

  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Attach Leave Status To Tree
  const attachLeaves = (node, leavesMap) => ({
    ...node,
    leaveStatus: leavesMap[node.id] || null,
    children: node.children
      ? node.children.map((child) => attachLeaves(child, leavesMap))
      : []
  });

  useEffect(() => {
    if (!userId || !orgId) return;

    const fetchData = async () => {
      try {
        const today = new Date().toISOString().split("T")[0];

        const [treeRes, leavesRes] = await Promise.all([
          axios.get(`${Base_URL}/Resource/team-tree/${userId}`),
          axios.get(
            `${Base_URL}/Leave/GetLeavesForToday/${orgId}?today=${today}`
          )
        ]);

        // Create Leave Map
        const leavesMap = {};
        leavesRes.data.forEach((item) => {
          leavesMap[item.resource_Id] = item.leaves;
        });

        // Attach Leave Status To Tree
        const updatedTree = attachLeaves(treeRes.data, leavesMap);
        setTree(updatedTree);

      } catch (err) {
        console.error("Failed to load team data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userId, orgId]);

  if (loading) return <p className="team-status">Loading team...</p>;
  if (!tree) return <p className="team-status">No team data found.</p>;

  return (
    <div className="team-details">
      <div className="team-card">

        {/* Header */}
        <div className="team-header">
          <div className="team-header-icon">👥</div>
          <div>
            <h4>Team Hierarchy</h4>
            <p>Your managers and direct reports</p>
          </div>
        </div>

        {/* ✅ Line Manager + Dot Line Manager (UNCHANGED) */}
        {(tree.lineManagerName || tree.dotLineManagerName) && (
          <div className="managers-row">
            {tree.lineManagerName && (
              <div className="manager-box lm">
                <span className="manager-tag">Line Manager</span>
                <span className="manager-name">{tree.lineManagerName}</span>
              </div>
            )}

            {tree.dotLineManagerName && (
              <div className="manager-box dlm">
                <span className="manager-tag">Dot Line Manager</span>
                <span className="manager-name">{tree.dotLineManagerName}</span>
              </div>
            )}
          </div>
        )}

        {/* ✅ Team Members Section */}
        {tree.children && tree.children.length > 0 && (
          <div className="team-section">
            <div className="team-section-label members-label">
              My team
            </div>

            <div className="children">
              {tree.children.map((child) => (
                <TreeNode key={child.id} node={child} />
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};


/* ============================= */
/* Recursive Node Component      */
/* ============================= */

const TreeNode = ({ node }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div className="tree-node">
      <div className={`node-card compact ${hasChildren ? "has-children" : ""}`}>

        {hasChildren ? (
          <span
            className={`toggle-btn ${open ? "open" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((prev) => !prev);
            }}
          >
            ▶
          </span>
        ) : (
          <span className="toggle-placeholder" />
        )}

        <strong>{node.name}</strong>

        {node.role && (
          <span className="node-role">{node.role}</span>
        )}

        {/* ✅ Leave Badge Added */}
        {node.leaveStatus && (
          <span
            className={`leave-badge ${node.leaveStatus
              .replace(/\s/g, "")
              .toLowerCase()}`}
          >
            {node.leaveStatus}
          </span>
        )}
      </div>

      <div className={`children-wrapper ${open ? "open" : ""}`}>
        <div className="children">
          {hasChildren &&
            node.children.map((child) => (
              <TreeNode key={child.id} node={child} />
            ))}
        </div>
      </div>
    </div>
  );
};

export default TeamDetails;
